import React from 'react';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Button, Card, Flex, Text, Input, Icon, InputProps } from '@opendoor/bricks/core';
import { Form } from '@opendoor/bricks/core/Form';
import { useSSEAContext } from 'sellDirect/SSEAContext';
import { useUpdateLeadPhoneNumber } from 'sellDirect/graphql/useUpdateLeadPhoneNumber';
import { SchedulingModalWithDateTime } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/SchedulingModalWithDateTime';
import { SchedulingModal } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/SchedulingModal';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';
import { ScheduleEaFormState } from 'sellDirect/routes/exterior-assessment/ExteriorAssessmentPage';
import { CONTACT_PHONE } from 'shared/helpers/globals';

interface ScheduleEaCardProps {
  formState: ScheduleEaFormState;
  onPhoneNumberChange: InputProps['onChange'];
  /** SSEA Context */
  sseaContext: ReturnType<typeof useSSEAContext>;
  /** Whether the in-person-ssea experiment is on/off */
  isInPersonSseaEnabled: boolean;
  /** Callback to use to update the lead phone number */
  updateLeadCustomerPhoneNumber: ReturnType<
    typeof useUpdateLeadPhoneNumber
  >['updateLeadCustomerPhoneNumber'];
}

/**
 * Card that handles the logic for scheduling, re-scheduling, and canceling an EA.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ScheduleEaCard: React.FC<ScheduleEaCardProps> = ({
  formState,
  onPhoneNumberChange,
  sseaContext,
  updateLeadCustomerPhoneNumber,
  isInPersonSseaEnabled,
}) => {
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = React.useState(false);
  const [isUpdatingSelection, setIsUpdatingSelection] = React.useState(false);
  const {
    selectedDate,
    earliestSlot,
    currentInspectionDate,
    availableExteriorAssessmentSlots,
    saveSelectedDate,
    cancelEA,
  } = sseaContext;

  if (!availableExteriorAssessmentSlots.length) {
    return null;
  }

  const shouldShowEarliestAvailableLabel =
    !currentInspectionDate || !!selectedDate?.equals(earliestSlot);

  const displayTime = selectedDate || currentInspectionDate || earliestSlot;

  /** Schedules an EA on submit */
  const onSubmit = async () => {
    try {
      await saveSelectedDate();
    } finally {
      // we don't want the phone number update to be block as HPM can do EA w/o seller present or at home
      // we also send out email comms
      formState.phoneNumber && (await updateLeadCustomerPhoneNumber(formState.phoneNumber));
    }
  };

  if (!!currentInspectionDate && !isUpdatingSelection) {
    return (
      <Card variant="tertiary" display="flex" flexDir="column" gridGap="7">
        <Flex gridGap="5" flexDir="row">
          <Flex w="24px" h="24px" alignItems="center" justifyContent="center">
            <Icon name="checkmark" color="green50" size={16} />
          </Flex>
          <Flex flexDir="column">
            <Text>Your Upcoming Selection</Text>
            {currentInspectionDate.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}
          </Flex>
        </Flex>
        <Flex gridGap="5">
          <Button
            aria-label={null}
            analyticsName="cake-exterior-assessment--cancel-assessment-button"
            variant="secondary"
            onClick={cancelEA}
            disabled={!currentInspectionDate}
          >
            Cancel
          </Button>
          <Button
            aria-label={null}
            variant="secondary"
            analyticsName="cake-exterior-assessment--update-date-button"
            onClick={() => setIsUpdatingSelection(true)}
          >
            Update date
          </Button>
        </Flex>
      </Card>
    );
  }

  return (
    <>
      <Card variant="tertiary" display="flex" flexDir="column" gridGap="7">
        {currentInspectionDate ? (
          <Card variant="outline" p={[3, 3, 3]}>
            <Flex gridGap="5" flexDir="row">
              <Flex w="24px" h="24px" alignItems="center" justifyContent="center">
                <Icon name="checkmark" color="green50" size={16} />
              </Flex>
              <Flex flexDir="column">
                <Text>Your current schedule</Text>
                {currentInspectionDate.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}
              </Flex>
            </Flex>
          </Card>
        ) : null}

        {/* Earliest availability */}
        <Flex flexDir="column" gridGap="5">
          <Card
            variant="primary"
            p={[7, 7, 7]}
            display="flex"
            flexDir="row"
            justifyContent="space-between"
          >
            <Box>
              <Text>
                {shouldShowEarliestAvailableLabel ? 'Next available time' : 'Your selected time'}
              </Text>
              {displayTime ? (
                <Text fontSize="s2" fontWeight="semibold">
                  {displayTime.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}
                </Text>
              ) : null}
            </Box>
            <Button
              analyticsName="cake-exterior-assessment--change-date-button"
              aria-label={null}
              variant="inline-link"
              onClick={() => setIsSchedulingModalOpen(true)}
              fontSize="s0"
              fontWeight="semibold"
              textDecorationLine="none"
              _hover={{
                textDecorationLine: 'underline',
              }}
            >
              Change
            </Button>
          </Card>

          {/* TODO: This is a placeholder phone number. Update this phone number. */}
          <Text>
            Choose a time when you’ll be home so we can access the property easily and answer any
            questions. If you can’t be there during the assessment, contact your Opendoor Expert at
            {CONTACT_PHONE}.
          </Text>
        </Flex>

        {/* Phone number reminder */}
        <Form
          onSubmit={(event) => {
            event.preventDefault();

            // Prevent submission if phone number is not valid
            if (!formState.isPhoneNumberValid) {
              showToast(
                'error',
                'Please enter a valid phone number',
                'cake-external-assessment-form--invalid-phone-number',
                { toastOptions: { position: 'top-center' } },
              );
              return;
            }

            onSubmit();
          }}
        >
          <Input
            name="exterior-assessment-phone-number"
            analyticsName="cake-exterior-assessment--phone-number-input"
            label="We’ll text a reminder to"
            id="cake-ea-phone-number"
            type="tel"
            placeholder="000-000-0000"
            onChange={onPhoneNumberChange}
            unboundedWidth
            value={formState.phoneNumber}
          />
          <Text fontSize="s00" mt="5" mb="8">
            By clicking “Confirm”, I consent to receive marketing calls and texts from Opendoor and
            its affiliates to the number provided, including by automated means. I may opt out at
            any time and consent is not a condition of purchase. Your information is protected by
            our Privacy Policy.
          </Text>

          <Flex gridGap="5">
            {currentInspectionDate ? (
              <Button
                aria-label={null}
                analyticsName="cake-exterior-assessment--close-button"
                variant="secondary"
                onClick={() => setIsUpdatingSelection(false)}
              >
                Close
              </Button>
            ) : null}
            <Button
              aria-label={null}
              analyticsName="cake-exterior-assessment--confirm-button"
              variant="primary"
              type="submit"
              disabled={!formState.isPhoneNumberValid}
            >
              Confirm
            </Button>
          </Flex>
        </Form>
      </Card>

      {isInPersonSseaEnabled ? (
        <SchedulingModalWithDateTime
          setIsOpen={setIsSchedulingModalOpen}
          isOpen={isSchedulingModalOpen}
        />
      ) : (
        <SchedulingModal setIsOpen={setIsSchedulingModalOpen} isOpen={isSchedulingModalOpen} />
      )}
    </>
  );
};
